import {
    Box,
    Checkbox,
    Divider,
    Grid2 as Grid,
    List,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from "@mui/material"
import { useEffect } from "react"
import { AcquisitionMode, AoiSpecs, ModeShape } from "../../models/AcquisitionMode"
import { useAcquisitionModeStore } from "../../zstore/AcquisitionModeStore"
import { useProjectEditStore } from "../../zstore/ProjectEditStore"
import { Label, Subtitle2 } from "../Typography/Typography"
import { ModeView } from "./ModeView"

interface ModeTabProps {
    modes: AcquisitionMode[]
    aoiSpecs: AoiSpecs
    modeSelectionDisabled?: boolean
}

export const ModeTabs = ({ modes, aoiSpecs, modeSelectionDisabled }: ModeTabProps) => {
    const updateSelectedAoi = useProjectEditStore((state) => state.updateAoiField)
    const setAvailableModes = useAcquisitionModeStore((state) => state.setAvailableModes)
    const selectedModeIds = useAcquisitionModeStore((state) => state.selectedModeIds)
    const modesByType = useAcquisitionModeStore((state) => state.modesByType)
    const setSelectedModes = useAcquisitionModeStore((state) => state.setSelectedModes)
    const viewAcqMode = useAcquisitionModeStore((state) => state.viewAcqMode)
    const setViewAcqMode = useAcquisitionModeStore((state) => state.setViewAcqMode)
    const aoi_mode = useAcquisitionModeStore((state) => state.aoiMode)
    const setAoiSpecsCache = useAcquisitionModeStore((state) => state.setAoiSpecsCache)

    useEffect(() => {
        setAoiSpecsCache(aoiSpecs)
    }, [setAoiSpecsCache, aoiSpecs])

    useEffect(() => {
        setAvailableModes(modes)
    }, [setAvailableModes, modes])

    const handleToggle = async (value: AcquisitionMode) => {
        const currentIndex = aoi_mode.findIndex((mode) => mode.mode_id === value.mode_id)

        const updatedAoiMode: ModeShape[] = [...aoi_mode]
        if (currentIndex === -1) {
            const createMode = ModeShape.createEmptyModeForm(value)
            updatedAoiMode.push(createMode)
        } else {
            updatedAoiMode.splice(currentIndex, 1)
        }

        const newChecked = updatedAoiMode.map((mode) => mode.mode_id)
        setSelectedModes(newChecked, updatedAoiMode)
        await updateSelectedAoi("aoi_mode", updatedAoiMode)
        await updateSelectedAoi("mode_ids", newChecked)
    }

    const modeIsChecked = (modeId: number) => selectedModeIds?.indexOf(modeId) !== -1

    return (
        <>
            <Label disableGutter>Select Acquisition Modes</Label>
            <Subtitle2 gutterBottom={"md"}>Required</Subtitle2>
            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <List disablePadding>
                        {modesByType.map((modesObj, idx) => (
                            <Box key={idx}>
                                <Typography>
                                    <strong>{modesObj.mode_type}</strong>
                                </Typography>
                                <Divider />
                                {modesObj.modes.map((value, idx) => {
                                    const labelId = `checkbox-list-secondary-label-${value.mode_id}`
                                    const selected = viewAcqMode?.mode_id === value.mode_id
                                    return (
                                        <ListItemButton
                                            key={idx}
                                            onClick={() => setViewAcqMode(value)}
                                            selected={selected}
                                        >
                                            <ListItemText id={labelId} primary={value.mode_name} />
                                            <ListItemSecondaryAction>
                                                <Checkbox
                                                    disabled={modeSelectionDisabled}
                                                    edge="end"
                                                    onClick={() => {
                                                        handleToggle(value)
                                                        setViewAcqMode(value)
                                                    }}
                                                    checked={modeIsChecked(value.mode_id)}
                                                    slotProps={{ input: { "aria-labelledby": labelId } }}
                                                />
                                            </ListItemSecondaryAction>
                                        </ListItemButton>
                                    )
                                })}
                            </Box>
                        ))}
                    </List>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 9 }}>
                    {viewAcqMode && (
                        <ModeView selectedMode={viewAcqMode} checked={modeIsChecked(viewAcqMode.mode_id)} />
                    )}
                </Grid>
            </Grid>
        </>
    )
}
